import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import PaperLayout from './PaperLayout';
import supabase from '../../supabase/supabase';

const MAX_REPLY_CHARS = 200;

const InviteResponse = ({ invite, onAccept, onDecline, onMaybe, onReply, sending, replyError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [showReply, setShowReply] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicMuted, setMusicMuted] = useState(false);
  const audioRef = useRef(null);

  const isSingles = invite?.flow_type === 'singles';
  const isCouples = invite?.flow_type === 'couples';
  const senderName = invite?.sender_profile?.name || invite?.sender_display_name || 'Someone special';

  // Auto-play music when the envelope is opened
  useEffect(() => {
    let audio = null;

    const tryPlayMusic = async () => {
      if (!invite?.music_track || !supabase) return;

      try {
        const { data: track } = await supabase
          .from('music_tracks')
          .select('audio_url')
          .eq('id', invite.music_track)
          .maybeSingle();

        if (track?.audio_url) {
          audio = new Audio(track.audio_url);
          audio.volume = 0.4;
          audio.loop = true;
          audioRef.current = audio;
          await audio.play();
          setMusicPlaying(true);
        }
      } catch (e) {
        console.log('Autoplay blocked, user can enable music');
      }
    };

    tryPlayMusic();

    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [invite?.music_track]);

  const toggleMusic = () => {
    if (audioRef.current) {
      if (musicMuted) {
        audioRef.current.muted = false;
        audioRef.current.volume = 0.4;
        setMusicMuted(false);
      } else {
        audioRef.current.muted = true;
        audioRef.current.volume = 0;
        setMusicMuted(true);
      }
    }
  };

  const requireAuth = (callback) => {
    if (!isAuthenticated) {
      localStorage.setItem('pendingInviteToken', invite.unique_token);
      localStorage.setItem('pendingInviteAction', JSON.stringify({ response: selectedResponse, message: replyMessage }));
      const inviteInfo = encodeURIComponent(JSON.stringify({ sender: senderName, occasion: invite?.occasion || 'something special' }));
      navigate(`/signup?invite=${invite.unique_token}&info=${inviteInfo}`);
      return;
    }
    callback();
  };

  const handleResponse = (type) => {
    setSelectedResponse(type);
    setShowReply(true);
  };

  const handleSendReply = () => {
    requireAuth(() => {
      if (onReply) {
        onReply(selectedResponse, replyMessage);
      } else if (selectedResponse === 'yes' && onAccept) {
        onAccept();
      } else if (selectedResponse === 'maybe' && onMaybe) {
        onMaybe(replyMessage);
      } else if (selectedResponse === 'no' && onDecline) {
        onDecline();
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-2">
      {/* Music control floating button */}
      {invite?.music_title && (
        <motion.button
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          onClick={toggleMusic}
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg text-xs font-medium transition-all ${
            musicPlaying && !musicMuted
              ? 'bg-pink-500 text-white'
              : 'bg-white text-gray-500 border border-gray-200'
          }`}
        >
          {musicPlaying && !musicMuted ? (
            <>
              <div className="flex gap-0.5">
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" />
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.15s' }} />
                <div className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
              </div>
              <span>♪ {invite.music_title}</span>
            </>
          ) : (
            <>
              <SafeIcon name={musicMuted ? 'VolumeX' : 'Music'} className="w-3.5 h-3.5" />
              <span>{musicMuted ? 'Music Muted' : '♪ Play Music'}</span>
            </>
          )}
        </motion.button>
      )}

      <PaperLayout invite={invite}>
        {/* Response section */}
        {!showReply ? (
          <div className="space-y-3">
            {isSingles ? (
              <>
                <button
                  onClick={() => handleResponse('yes')}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-green-200 text-lg flex items-center justify-center gap-2"
                >
                  <span className="text-2xl">❤️</span> Yes! I'd love to!
                </button>
                <button
                  onClick={() => handleResponse('maybe')}
                  className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-amber-200 text-lg flex items-center justify-center gap-2"
                >
                  <span className="text-2xl">🤔</span> Maybe...
                </button>
                <button
                  onClick={() => handleResponse('no')}
                  className="w-full bg-white text-gray-500 font-semibold py-4 rounded-xl border border-gray-200 text-lg flex items-center justify-center gap-2"
                >
                  <span className="text-2xl">❌</span> No, thanks
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleResponse('yes')}
                  className={`w-full bg-gradient-to-r ${
                    isCouples ? 'from-purple-500 to-violet-500 shadow-purple-200' : 'from-pink-500 to-rose-500 shadow-pink-200'
                  } text-white font-bold py-4 rounded-xl shadow-lg text-lg flex items-center justify-center gap-2`}
                >
                  <span className="text-2xl">❤️</span> Yes! I love this!
                </button>
                <button
                  onClick={() => handleResponse('reply')}
                  className="w-full bg-white text-gray-600 font-semibold py-4 rounded-xl border border-gray-200 flex items-center justify-center gap-2"
                >
                  <SafeIcon name="MessageCircle" /> Write a Reply
                </button>
              </>
            )}

            {!isAuthenticated && (
              <p className="text-center text-xs text-gray-400 mt-2">
                You'll be asked to sign up to send your response 💌
              </p>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {selectedResponse && selectedResponse !== 'reply' && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                selectedResponse === 'yes' ? 'bg-green-100 text-green-700' :
                selectedResponse === 'maybe' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {selectedResponse === 'yes' && '❤️ Yes!'}
                {selectedResponse === 'maybe' && '🤔 Maybe'}
                {selectedResponse === 'no' && '❌ No'}
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <label className="block text-xs text-gray-400 font-semibold uppercase mb-2">
                Your Reply to {senderName}
              </label>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value.slice(0, MAX_REPLY_CHARS))}
                placeholder={
                  selectedResponse === 'yes' ? "I'd love to! Can't wait! ❤️" :
                  selectedResponse === 'maybe' ? "I'm interested but need more details..." :
                  selectedResponse === 'no' ? "Thanks for thinking of me, but..." :
                  "Write your heartfelt reply..."
                }
                rows={3}
                className="w-full p-3 rounded-xl border border-gray-100 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none resize-none text-sm text-gray-700"
              />
              <p className="text-[10px] text-gray-400 text-right mt-1">{replyMessage.length}/{MAX_REPLY_CHARS}</p>
            </div>

            {/* Error message */}
            {replyError && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2"
              >
                <SafeIcon name="AlertCircle" className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{replyError}</p>
              </motion.div>
            )}

            <button
              onClick={handleSendReply}
              disabled={sending}
              className={`w-full bg-gradient-to-r ${
                isCouples ? 'from-purple-500 to-violet-500 shadow-purple-200' : 'from-pink-500 to-rose-500 shadow-pink-200'
              } text-white font-bold py-4 rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity`}
            >
              {sending ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <SafeIcon name="Send" /> Send Reply 💌
                </>
              )}
            </button>

            <button
              onClick={() => { setShowReply(false); setSelectedResponse(null); }}
              disabled={sending}
              className="w-full text-gray-500 font-medium py-2 hover:text-pink-500 transition-colors text-sm disabled:opacity-50"
            >
              ← Back to options
            </button>
          </motion.div>
        )}
      </PaperLayout>
    </div>
  );
};

export default InviteResponse;